/* Volatile - filter rail + refine dock wiring.
 *
 * Three jobs, all of them additive. Nothing here opens or closes the filter
 * sheet: `sections/collection.liquid` and `sections/search.liquid` already
 * listen for `[data-open-filters]` and own the dialog, the AJAX navigation
 * and the apply/clear form. This file only decides WHAT the sheet shows once
 * that code has opened it.
 *
 *   1. `--tz-bar-h` - the stuck header's measured height, published to
 *      <html> so the sticky pill rail can dock directly beneath it. The
 *      height is not a constant: it changes with the title's wrap, with the
 *      viewport, and between the bar's expanded and compacted states.
 *   2. `data-filter-mode` - FILTER or SORT, from the split dock. Puts the
 *      sheet into that mode, which shows one panel and titles the sheet
 *      with it.
 *   3. `data-filter-group` - from a rail pill. Collapses every other group
 *      in the sheet, expands this one, and scrolls it to the top.
 *
 * Load order matters and is deliberate. The sections' handlers are inline
 * scripts that register during parsing; this file is `defer`, so it always
 * registers later and therefore runs later in the same click dispatch - by
 * the time these handlers fire, the dialog is already open.
 */
(function () {
  'use strict';

  var lastMode = 'filter';

  function sheet() {
    return document.querySelector('[data-filter-drawer]');
  }

  /* Mirrors the sections' own `switchTab`, plus a `data-mode` attribute the
     stylesheet keys off to hide the inactive tab and let the active one read
     as the sheet's title. Restated rather than called: `switchTab` is a
     closure inside each section's IIFE and is not reachable from here. */
  function applyMode(dlg, mode) {
    if (!dlg) return;
    dlg.setAttribute('data-mode', mode);
    dlg.querySelectorAll('[data-tab]').forEach(function (b) {
      var on = b.getAttribute('data-tab') === mode;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    dlg.querySelectorAll('[data-panel]').forEach(function (p) {
      var on = p.getAttribute('data-panel') === mode;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });
  }

  /* The pill and the group find each other through the key both
     `snippets/collection-filter-rail.liquid` and
     `snippets/collection-filters-2col.liquid` derive the same way, so
     neither file has to know the other's markup. */
  function openGroup(dlg, key) {
    if (!dlg || !key) return;
    var tab = dlg.querySelector('[data-f2-tab="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
    if (tab) selectGroup(dlg, key);
  }

  /* Left column selects, right column swaps. Both are driven from the key
     rather than from an index, so a group that Shopify stops returning
     simply has no tab and nothing goes out of step. */
  function selectGroup(dlg, key) {
    dlg.querySelectorAll('[data-f2-tab]').forEach(function (b) {
      var on = b.getAttribute('data-f2-tab') === key;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    dlg.querySelectorAll('[data-f2-pane]').forEach(function (p) {
      var on = p.getAttribute('data-f2-pane') === key;
      p.hidden = !on;
      if (on) p.scrollTop = 0;
    });
  }

  /* Mirrors each group's live count into the left column, and keeps its
     Select All in the right one of its three states. `recountActive` in the
     section owns the hidden `.v-fg__count` this reads - running after it in
     the same event is enough, and is why this listens on the sheet rather
     than on the inputs. */
  function syncCounts(dlg) {
    if (!dlg) return;
    dlg.querySelectorAll('.v-f2__pane').forEach(function (pane) {
      var key = pane.getAttribute('data-f2-pane');
      var boxes = pane.querySelectorAll('.v-fg__checkbox');
      var checked = pane.querySelectorAll('.v-fg__checkbox:checked').length;

      var priced = 0;
      pane.querySelectorAll('input[type="number"]').forEach(function (i) {
        if (i.value !== '') priced += 1;
      });

      var total = checked + priced;
      var badge = dlg.querySelector('[data-f2-count="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
      if (badge) badge.textContent = total > 0 ? String(total) : '';

      var all = pane.querySelector('[data-f2-all]');
      if (all && boxes.length) {
        all.checked = checked === boxes.length;
        all.indeterminate = checked > 0 && checked < boxes.length;
      }
    });
  }

  /* ── The URL is the only source of truth ──────────────────────────────
     The sheet is a <dialog> that lives OUTSIDE `[data-collection]`, so the
     section's AJAX swap - which replaces the section's innerHTML and with it
     the rail - never touches it. Left alone, its checkboxes keep whatever
     the customer last ticked, applied or not: tick two sizes, close with the
     ✕, and the sheet goes on claiming "Filter (2)" while the rail, the grid
     and the URL all correctly say nothing is on.

     So the sheet is re-derived from `location.search` at every point where
     the two could have drifted: on open, on close, after a swap, and on
     back/forward. `defaultChecked` is set alongside `checked` so the
     baseline moves too, and the section's own `recountActive` is triggered
     with a bubbled change event to bring the header and Apply counts along.

     This cannot go stale the way a snapshot taken at open time would: it
     reads the address bar, which is what actually produced the grid. */
  /* ── The rail, painted from the URL ───────────────────────────────────
     The pills are rendered by Liquid, so they are only correct as long as
     the section actually re-renders. That is one link in a chain - build the
     URL, fetch the section, find `[data-collection]`, swap it - and if any
     link fails quietly the pills sit there showing the state from before the
     filter was applied, which is exactly what Aditya is seeing.
     So they are also painted here, straight from `location.search`, with no
     server round trip and nothing to go stale.

     The group key needs no lookup table: it is the filter's `param_name` put
     through Liquid's `handle`, which lowercases and turns `.` into `-` while
     leaving `_` alone. `filter.v.option.size` -> `filter-v-option-size`. */
  function railKey(param) {
    return String(param).toLowerCase().replace(/\./g, '-');
  }

  function paintRail() {
    var rail = document.querySelector('[data-col-rail]');
    if (!rail || typeof URLSearchParams !== 'function') return;

    var counts = {};
    var total = 0;
    new URLSearchParams(window.location.search).forEach(function (v, k) {
      if (k.indexOf('filter.') !== 0) return;
      /* Price is out of the UI, so a bound left in an old URL must not
         light up a pill that no longer exists. */
      if (k.indexOf('price') !== -1) return;
      var key = railKey(k);
      counts[key] = (counts[key] || 0) + 1;
      total += 1;
    });

    rail.querySelectorAll('.v-col__pill[data-filter-group]').forEach(function (pill) {
      var n = counts[pill.getAttribute('data-filter-group')] || 0;
      pill.classList.toggle('is-active', n > 0);
      var badge = pill.querySelector('.v-col__pill-count');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'v-col__pill-count';
          pill.insertBefore(badge, pill.firstChild);
        }
        badge.textContent = String(n);
      } else if (badge) {
        badge.parentNode.removeChild(badge);
      }
    });

    var all = rail.querySelector('.v-col__pill--all');
    if (all) {
      all.classList.toggle('is-active', total > 0);
      var dot = all.querySelector('.v-col__pill-dot');
      if (total > 0 && !dot) {
        dot = document.createElement('span');
        dot.className = 'v-col__pill-dot';
        dot.setAttribute('aria-hidden', 'true');
        all.appendChild(dot);
      } else if (total === 0 && dot) {
        dot.parentNode.removeChild(dot);
      }
    }

    /* Active-first, the same ordering the Liquid does in two passes. Walked
       in reverse so repeated inserts just after the separator land the
       group back in its original relative order. */
    var scroll = rail.querySelector('.v-col__rail-scroll');
    var sep = scroll && scroll.querySelector('.v-col__rail-sep');
    if (!scroll || !sep) return;
    var pills = Array.prototype.slice.call(scroll.querySelectorAll('.v-col__pill[data-filter-group]'));
    for (var i = pills.length - 1; i >= 0; i--) {
      if (pills[i].classList.contains('is-active')) scroll.insertBefore(pills[i], sep.nextSibling);
    }
  }

  function syncFromUrl() {
    paintRail();
    var dlg = sheet();
    if (!dlg || typeof URLSearchParams !== 'function') return;
    var params = new URLSearchParams(window.location.search);

    dlg.querySelectorAll('.v-fg__checkbox').forEach(function (c) {
      if (!c.name) return;
      var on = params.getAll(c.name).indexOf(c.value) !== -1;
      c.checked = on;
      c.defaultChecked = on;
    });

    /* Symmetric with the Liquid: no `sort_by` in the URL means the empty
       Recommended option, so the absent case is a real value here rather
       than a reason to skip. */
    var sort = params.get('sort_by') || '';
    dlg.querySelectorAll('.v-col-drawer__sort-input').forEach(function (r) {
      r.checked = r.value === sort;
      r.defaultChecked = r.checked;
    });

    syncCounts(dlg);
    /* Bubbles to the drawer, where the section binds `recountActive` - the
       only thing that owns the header total and the Apply count. */
    dlg.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-open-filters]');
    if (!trigger) return;
    var dlg = sheet();
    if (!dlg) return;

    lastMode = trigger.getAttribute('data-filter-mode') || 'filter';
    applyMode(dlg, lastMode);
    syncFromUrl();
    lockScroll(true);

    var group = trigger.getAttribute('data-filter-group');
    if (group) openGroup(dlg, group);
  });

  /* Left-column group switching. */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-f2-tab]');
    if (!tab) return;
    var dlg = tab.closest('[data-filter-drawer]');
    if (!dlg) return;
    e.preventDefault();
    selectGroup(dlg, tab.getAttribute('data-f2-tab'));
  });

  /* Select All. Disabled values are skipped: they are the ones Shopify says
     would return nothing, and checking them would build a URL that lands on
     an empty grid. */
  document.addEventListener('change', function (e) {
    var all = e.target.closest('[data-f2-all]');
    if (!all) return;
    var pane = all.closest('.v-f2__pane');
    var dlg = all.closest('[data-filter-drawer]');
    if (!pane) return;
    pane.querySelectorAll('.v-fg__checkbox').forEach(function (c) {
      if (!c.disabled) c.checked = all.checked;
    });
    /* Let the section's own `recountActive` see the new state. */
    pane.dispatchEvent(new Event('change', { bubbles: true }));
    syncCounts(dlg);
  });

  /* Any other change inside the sheet: keep the left column and the Select
     All boxes in step. */
  document.addEventListener('change', function (e) {
    var dlg = e.target.closest && e.target.closest('[data-filter-drawer]');
    if (!dlg || e.target.closest('[data-f2-all]')) return;
    syncCounts(dlg);
  });

  /* Per-group value search. Substring, not prefix: people type the memorable
     middle of a word as often as the start of one. */
  document.addEventListener('input', function (e) {
    var box = e.target.closest('[data-f2-search]');
    if (!box) return;
    var pane = box.closest('.v-f2__pane');
    if (!pane) return;
    var q = box.value.trim().toLowerCase();
    pane.querySelectorAll('.v-f2__row[data-f2-value]').forEach(function (row) {
      row.hidden = q !== '' && row.getAttribute('data-f2-value').indexOf(q) === -1;
    });
    var allRow = pane.querySelector('.v-f2__row--all');
    if (allRow) allRow.hidden = q !== '';
  });

  /* ── Scroll lock ──────────────────────────────────────────────────────
     `showModal()` blocks interaction with the page behind it but does not
     reliably stop the document scrolling on iOS, where the touch bleeds
     through to the body. The class does that; `overscroll-behavior` on the
     sheet's own columns keeps their scroll from chaining out to it. */
  function lockScroll(on) {
    document.documentElement.classList.toggle('tz-sheet-open', !!on);
  }

  /* ── Apply always lands ───────────────────────────────────────────────
     The section applies filters over the Section Rendering API, and its
     handler bails silently on any surprise - a section id that does not
     resolve, a payload without the key it asked for - leaving the sheet
     closed, the URL untouched and the grid exactly as it was. From the
     customer's side that is indistinguishable from the filter doing
     nothing.

     Capture phase, so this runs BEFORE the section's own bubble-phase
     handler on `document`: that ordering is what lets `submitting` be set
     in time for the `close` that the section fires a few lines later, and
     it is why the reset below does not untick the boxes being submitted.

     The fallback checks, once, whether the address bar caught up. If it did
     not, it does the SAME swap itself rather than reloading - a reload would
     throw away the scroll position and flash the whole page for what is
     meant to be an in-place filter. Only if that second attempt also fails
     does it fall through to a real navigation, which is the last honest
     option left. */
  function fallbackSwap(url) {
    var section = document.querySelector('[data-collection]');
    var wrap = section && section.closest('.shopify-section');
    var id = wrap && wrap.id ? wrap.id.replace(/^shopify-section-/, '') : '';
    if (!section || !id || typeof fetch !== 'function') {
      window.location.assign(url);
      return;
    }
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    /* No `Accept: application/json` - see the note in
       `sections/collection.liquid`. That header makes a /collections/* URL
       answer with the Collection JSON API instead of the rendered section. */
    fetch(url + sep + 'sections=' + encodeURIComponent(id), {
      credentials: 'same-origin'
    })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .then(function (data) {
        var html = data[id];
        if (!html || typeof html !== 'string') throw new Error('no section');
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-collection]');
        if (!fresh) throw new Error('no [data-collection]');
        /* Theirs landed while this request was out. Leave it alone. */
        if (pendingUrl !== url) return;
        pendingUrl = '';
        section.innerHTML = fresh.innerHTML;
        window.history.pushState({ collectionAjax: true }, '', url);
        section.classList.remove('is-loading');
        syncFromUrl();
      })
      .catch(function () { window.location.assign(url); });
  }

  var submitting = false;
  var pendingUrl = '';

  function buildUrl(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (v, k) {
      if (v === '' || v == null) return;
      params.append(k, v);
    });
    var qs = params.toString();
    return form.action.split('?')[0] + (qs ? '?' + qs : '');
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-filter-form]');
    if (!form) return;
    submitting = true;
    lockScroll(false);

    var url = buildUrl(form);
    pendingUrl = url;
    var want = url.split('?')[1] || '';

    /* 700ms: comfortably past a healthy section fetch (150-400ms on this
       store) and short enough that a dead one does not read as a hang. A
       duplicate request is the whole cost of guessing early. */
    window.setTimeout(function () {
      if (pendingUrl !== url) return;              // the swap already landed
      var have = window.location.search.replace(/^\?/, '');
      if (have === want) { pendingUrl = ''; return; }
      fallbackSwap(url);
    }, 700);
  }, true);

  /* Every close path lands here: the ✕ and the backdrop both call
     `dialog.close()`, ESC closes natively, and the section's submit handler
     closes before it navigates. Capture phase, because a <dialog>'s `close`
     event does not bubble. */
  document.addEventListener('close', function (e) {
    if (!e.target || !e.target.matches || !e.target.matches('[data-filter-drawer]')) return;
    lockScroll(false);
    /* Not while applying: the URL has not changed yet, so re-deriving now
       would untick the very boxes being submitted, in view, mid-animation.
       The swap that follows re-derives from the new URL instead. */
    if (submitting) { submitting = false; return; }
    syncFromUrl();
  }, true);

  window.addEventListener('popstate', function () {
    requestAnimationFrame(syncFromUrl);
  });

  /* Tapping a tab inside the sheet still switches panels via the section's
     own handler; keep `data-mode` in step so the title follows. */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-tab]');
    if (!tab || !tab.closest('[data-filter-drawer]')) return;
    lastMode = tab.getAttribute('data-tab') || 'filter';
    var dlg = sheet();
    if (dlg) dlg.setAttribute('data-mode', lastMode);
  });

  /* ── Sticky offset ────────────────────────────────────────────────────
     Only measured while the bar is stuck. Expanded it is a different height
     (and on album pages its inner block is display:none, so it is nearly
     zero), and docking the rail to that would leave a gap or an overlap the
     moment the bar compacts. Until the first stick the CSS fallback holds. */
  var lastBarH = 0;
  function measureBar() {
    var bar = document.querySelector('[data-col-bar]');
    if (!bar || !bar.classList.contains('is-stuck')) return;
    var h = Math.round(bar.getBoundingClientRect().height);
    if (h <= 0) return;
    /* Only write on a REAL change. This runs once per animation frame for
       as long as the page is scrolling, and `--tz-bar-h` feeds the sticky
       `top` of `.v-col__rail`, which transitions. Re-writing the same value
       every frame restarted that transition every frame while the bar was
       mid-move, so the rail crept instead of travelling - part of what made
       the header look like it could not decide where to sit (Aditya,
       2026-09-14, collection-page stutter recording). A 2px deadband also
       absorbs the sub-pixel wobble a transitioning bar reports. */
    if (Math.abs(h - lastBarH) < 2) return;
    lastBarH = h;
    document.documentElement.style.setProperty('--tz-bar-h', h + 'px');
  }

  /* ── No slide-in on a bar that is already meant to be gone ────────────
     Scrolling down sets `tz-chrome-hidden` on <html> after about 6px, well
     before the sentinel trips `is-stuck` on the bar. The bar therefore
     becomes stuck while the page is ALREADY in its hidden state, and the
     hide rule - which needs both classes - then animates it from
     visible-and-pinned to off-screen. That is the flash: the header
     appearing for half a second on its way out.

     Watching the bar's own class attribute catches the moment `is-stuck`
     lands. MutationObserver callbacks run as microtasks, before the style
     recalc that would start the transition, so adding `tz-no-anim` here
     stops it before it has a frame to render rather than cutting it short.
     One frame later the class comes off and the bar animates normally
     again - so scrolling back UP still slides it in. */
  function watchBarFlash() {
    if (typeof MutationObserver !== 'function') return;
    var bar = document.querySelector('[data-col-bar]');
    if (!bar) return;

    new MutationObserver(function () {
      if (!bar.classList.contains('is-stuck')) return;
      if (!document.documentElement.classList.contains('tz-chrome-hidden')) return;
      if (bar.classList.contains('tz-no-anim')) return;
      bar.classList.add('tz-no-anim');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { bar.classList.remove('tz-no-anim'); });
      });
    }).observe(bar, { attributes: true, attributeFilter: ['class'] });
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      measureBar();
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', measureBar, { passive: true });

  /* The section replaces its own innerHTML on every filter, sort and page
     change, which throws away the dialog this file just configured. Re-apply
     the mode to the replacement so an open sheet does not snap back to the
     filter panel after an Apply from the sort panel. */
  function watchSwaps() {
    var section = document.querySelector('[data-collection]');
    if (!section || typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      /* The swap landed, so the fallback navigation is not needed. */
      pendingUrl = '';
      var dlg = sheet();
      if (dlg && dlg.open) applyMode(dlg, lastMode);
      /* The swap just replaced the rail from the server's view of the URL.
         Bring the sheet - which the swap does not touch - to the same view,
         and repaint the pills so the two cannot disagree. */
      syncFromUrl();
      if (!dlg || !dlg.open) lockScroll(false);
      measureBar();
      /* The swap replaced the bar, so the old observer is watching a
         detached node. Same for the rail's pinned observer. */
      watchBarFlash();
      watchRailPinned();
    }).observe(section, { childList: true, subtree: false });
  }

  /* ── Is the rail pinned? ──────────────────────────────────────────────
     Publishes `tz-col-stuck` on <html> for the moment the sticky rail stops
     travelling with the page and parks on the nav row. Desktop keys three
     things off it: the rail tightens to clear the nav pills, and the album
     title and its divider slide out from behind the wordmark.

     Watching the RAIL, not the header sentinel that `.is-stuck` uses. The
     sentinel sits above the lede in source order, so it leaves the viewport
     while the album details are still fully on screen - the title would have
     appeared in the blob long before the page had scrolled anywhere near it.
     The rail reaching the top is the honest signal, because that IS the
     moment the blob forms.

     The mechanism: a root inset one pixel deeper than the rail's own `top`.
     While the rail is in flow below that line it is fully inside the root
     and the ratio is 1; the instant it pins it is clipped by that inset and
     the ratio drops. `threshold: [1]` is what makes the callback fire on
     exactly that transition. */
  function watchRailPinned() {
    var rail = document.querySelector('[data-col-rail]');
    var root = document.documentElement;
    if (!rail || typeof IntersectionObserver !== 'function') {
      root.classList.remove('tz-col-stuck');
      return;
    }
    /* Read the offset the CSS actually chose rather than hard-coding 20px,
       so m-web's own rail offsets keep working through the same code. */
    var top = parseFloat(getComputedStyle(rail).top);
    if (!isFinite(top)) top = 0;
    new IntersectionObserver(function (entries) {
      root.classList.toggle('tz-col-stuck', entries[0].intersectionRatio < 1);
    }, { threshold: [1], rootMargin: '-' + (top + 1) + 'px 0px 0px 0px' }).observe(rail);
  }

  function boot() {
    watchSwaps();
    watchBarFlash();
    watchRailPinned();
    syncFromUrl();
    measureBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
